import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, AuthState } from '../types';
import { api } from '../services/api';

interface ExtendedAuthState extends AuthState {
  updateProfile: (data: Partial<User>) => Promise<boolean>;
  sendOtp: (phone: string) => Promise<{ success: boolean; message?: string; error?: string }>;
}

const AuthContext = createContext<ExtendedAuthState | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing session
    const currentUser = api.getCurrentUser();
    if (currentUser) {
      setUser(currentUser);
      setIsAuthenticated(true);
    }
    setLoading(false);
  }, []);

  const sendOtp = async (phone: string) => {
    try {
      return await api.sendOtp(phone);
    } catch (error) {
      console.error("Send OTP failed", error);
      return { success: false, error: "خطا در برقراری ارتباط با سرور" };
    }
  };

  const login = async (phone: string, otp: string, method: 'phone' | 'google' = 'phone') => {
    try {
      const loggedUser = await api.login(phone, otp, method);
      if (loggedUser) {
        setUser(loggedUser);
        setIsAuthenticated(true);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Login failed", error);
      return false;
    }
  };

  const logout = () => {
    api.logout();
    setUser(null);
    setIsAuthenticated(false);
  };

  const updateProfile = async (data: Partial<User>) => {
    if (!user) return false;
    try {
      const updatedUser = await api.updateUser(user.id, data);
      if (updatedUser) {
        setUser(updatedUser);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Update profile failed", error);
      return false;
    }
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, login, logout, updateProfile, sendOtp }}>
      {loading ? (
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-600"></div>
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};